/**
 * Experience levels — the two views of Karr, and the brain behind the adaptive UI.
 *
 * There are exactly two, and they are named after **what they show, not who you
 * are** (`docs/ONBOARDING.md` O3):
 *
 *   - **Simple**   — the six-row surface and the chronological Home. Plain
 *                    language, no journal entries, nothing to self-assess.
 *   - **Standard** — the full accounting toolkit.
 *
 * This file used to open by naming three personas — "Business Owner", "Finance
 * Manager", "Accountant / CA" — and ask a new user to pick which one they *were*.
 * ob-1 replaced that question with an intent question, ga-7 collapsed three views
 * to two, and ob-6 took the last of those words out of the product: the mode
 * dropdown, the Settings selector and the profile-menu tooltip were each still
 * printing "Business Owner" next to "Simple".
 *
 * The stored value `business_owner` survives as an id in localStorage and on
 * `orgs.experience_level`; it is not a name anyone reads, and renaming it would
 * silently reset every user's view. `finance_manager` is the retired middle
 * view — `useExperienceLevel` maps it to Standard on read.
 *
 * Each level controls:
 *   1. Which sidebar items are visible
 *   2. How accounting terms are translated (jargon → plain English)
 *   3. Which features are highlighted/hidden
 *
 * @since 2.0.0
 * @since ob-6 (2026-07-29) — one view, one name.
 */

// ─────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────

import { SIMPLE_REACHABLE_LEAF_IDS } from './simpleNav';

export type ExperienceLevel = 'business_owner' | 'finance_manager' | 'expert';

/**
 * Icon name from lucide-react — consumers import the icon by name.
 * We store the string key here rather than the component itself to keep
 * this config file free of React imports.
 */
export type LevelIconName = 'Zap' | 'BarChart3' | 'Shield';

export interface ExperienceLevelMeta {
    id: ExperienceLevel;
    /**
     * The view's name. **One field, on purpose** (ob-6): this used to be a pair
     * — `label` ("Business Owner") and `displayLabel` ("Simple") — and three
     * screens rendered both, side by side, so the retired persona name outlived
     * every decision to remove it. A view has one name.
     */
    label: string;
    /** Lucide icon name */
    iconName: LevelIconName;
    /** One line on what this view is for, used in tooltips. */
    description: string;
    /** Subtitle hint shown below the mode selector */
    hint: string;
}

// ─────────────────────────────────────────────
// Level Definitions
// ─────────────────────────────────────────────

// wv-5 — the copy stopped claiming the views differ in how MUCH they offer
// ("only what you need" / "all features" read as hiding, which is exactly what
// O5 forbids). The real difference is SHAPE and LANGUAGE over the same books.
// wv-6 (founder-picked, 2026-08-06) — the views are named **Flow** and **Books**:
// what each one shows, still never who you are (O3). The stored ids stay frozen.
export const EXPERIENCE_LEVELS: ExperienceLevelMeta[] = [
    {
        id: 'business_owner',
        label: 'Flow',
        iconName: 'Zap',
        description: 'Plain words, a few screens — your business in the order it happens',
        hint: 'Same books, simpler words',
    },
    // ga-7 — collapsed to two selectable views. 'finance_manager' is retired from
    // the chooser (stored values migrate to 'expert' on read); the enum value is
    // kept for backward-compat only. Books (= expert) is the full accountant view.
    {
        id: 'expert',
        label: 'Books',
        iconName: 'BarChart3',
        description: 'Journals, ledgers and every tool — the full books, all at once',
        hint: 'Same books, every tool on the table',
    },
];

export function getLevelMeta(level: ExperienceLevel): ExperienceLevelMeta {
    return EXPERIENCE_LEVELS.find(l => l.id === level) || EXPERIENCE_LEVELS[0];
}

// ─────────────────────────────────────────────
// Accounting Term Translator
// ─────────────────────────────────────────────
// Maps expert accounting terms to plain-language equivalents
// for each experience level.

interface TermTranslation {
    expert: string;
    finance_manager: string;
    business_owner: string;
}

const TERM_MAP: Record<string, TermTranslation> = {
    'accounts_receivable': { expert: 'Accounts Receivable', finance_manager: 'Receivables', business_owner: 'Money people owe you' },
    'accounts_payable': { expert: 'Accounts Payable', finance_manager: 'Payables', business_owner: 'Money you owe' },
    'credit_note': { expert: 'Credit Note', finance_manager: 'Credit Note', business_owner: 'Customer refund' },
    'debit_note': { expert: 'Debit Note', finance_manager: 'Debit Note', business_owner: 'Vendor return' },
    'journal_entry': { expert: 'Journal Entry', finance_manager: 'Journal Entry', business_owner: 'Manual adjustment' },
    'chart_of_accounts': { expert: 'Chart of Accounts', finance_manager: 'Chart of Accounts', business_owner: 'Account categories' },
    'bank_reconciliation': { expert: 'Bank Reconciliation', finance_manager: 'Bank Reconciliation', business_owner: 'Match bank transactions' },
    'trial_balance': { expert: 'Trial Balance', finance_manager: 'Trial Balance', business_owner: 'Account summary' },
    'profit_and_loss': { expert: 'Profit & Loss', finance_manager: 'Profit & Loss', business_owner: 'Profit report' },
    'balance_sheet': { expert: 'Balance Sheet', finance_manager: 'Balance Sheet', business_owner: 'What you own & owe' },
    'cash_flow': { expert: 'Cash Flow Statement', finance_manager: 'Cash Flow', business_owner: 'Money flow' },
    'invoice': { expert: 'Invoice', finance_manager: 'Invoice', business_owner: 'Invoice' },
    'bill': { expert: 'Bill', finance_manager: 'Bill', business_owner: 'Bill' },
    'estimate': { expert: 'Estimate', finance_manager: 'Estimate', business_owner: 'Price quote' },
    'purchase_order': { expert: 'Purchase Order', finance_manager: 'Purchase Order', business_owner: 'Order to supplier' },
    'delivery_challan': { expert: 'Delivery Challan', finance_manager: 'Delivery Challan', business_owner: 'Delivery slip' },
    'recurring_invoice': { expert: 'Recurring Invoice', finance_manager: 'Recurring Invoice', business_owner: 'Auto-repeat invoice' },
    'fixed_asset': { expert: 'Fixed Asset', finance_manager: 'Fixed Asset', business_owner: 'Business property' },
    'depreciation': { expert: 'Depreciation', finance_manager: 'Depreciation', business_owner: 'Value decrease over time' },
    'gst_return': { expert: 'GST Return', finance_manager: 'GST Return', business_owner: 'Tax filing' },
    'tds': { expert: 'TDS', finance_manager: 'TDS', business_owner: 'Tax deducted on payments' },
    'consolidation': { expert: 'Consolidation', finance_manager: 'Consolidation', business_owner: 'Combine companies' },
    'multi_currency': { expert: 'Multi-Currency', finance_manager: 'Multi-Currency', business_owner: 'Foreign currencies' },
    'receivables_payables': { expert: 'Receivables & Payables', finance_manager: 'Receivables & Payables', business_owner: 'Who owes whom' },
    'budgeting': { expert: 'Budgeting & Forecasts', finance_manager: 'Budgeting & Forecasts', business_owner: 'Spending plan' },
    'compliance': { expert: 'Compliance Calendar', finance_manager: 'Compliance Calendar', business_owner: 'Tax deadlines' },
    'payment_links': { expert: 'Payment Links', finance_manager: 'Payment Links', business_owner: 'Collect payments online' },
};

/**
 * Translate an accounting term to the user's language.
 *
 * @example
 *   t('accounts_receivable', 'business_owner') → "Money people owe you"
 *   t('accounts_receivable', 'expert')         → "Accounts Receivable"
 */
export function translateTerm(termKey: string, level: ExperienceLevel): string {
    const term = TERM_MAP[termKey];
    if (!term) return termKey;
    return term[level];
}

// ─────────────────────────────────────────────
// Sidebar Visibility Rules
// ─────────────────────────────────────────────
// Which nav item IDs are visible per experience level.
// Items NOT in this list for a level are hidden from that level's sidebar.

/**
 * Nav item IDs reachable in Simple mode — ob-2.
 *
 * DERIVED, never hand-kept: `simpleNav.ts` is the single list (O5). It covers
 * the six sidebar rows *plus* everything the in-screen hub strips carry,
 * because "visible in Simple" means a Simple user can get there — not that it
 * owns a sidebar row. Until ob-2 this was 27 ids maintained by hand alongside
 * the tree in sidebarNav.tsx, and the two had already drifted apart.
 */
const BUSINESS_OWNER_ITEMS: ReadonlySet<string> = SIMPLE_REACHABLE_LEAF_IDS;

/** Items visible to the retired middle view (`finance_manager`, dropped by ga-7 —
 *  stored values now read as Standard). Written out in full since ob-2: it used
 *  to spread the Simple set, which has since shrunk to six rows. */
const FINANCE_MANAGER_ITEMS = new Set([
    'dashboard',
    'my-businesses',
    'opening-balances',
    'sales',
    'pos',
    'contacts',
    'items',
    'expenses',
    'money-in',
    'money-out',
    'pay-week',
    'reports',
    'receipt-scanner',
    'ai-tools',
    'payment-links',
    'inventory',
    'gst-filing',
    'accruals',
    'books-health',
    'notices',
    'estimates',
    'credit-notes',
    'recurring-invoices',
    'purchases',
    'purchase-orders',
    'debit-notes',
    'recurring-bills',
    'goods-received',
    'transfers',
    'bank-statements',
    'bank-reconciliation',
    'bank-feeds',
    'journals',
    'accounts',
    'fixed-assets',
    'loans',
    'compliance-reports',
    'compliance',
    'report-builder',
    'receivables',
    'budgeting',
    'ngo-reports',
    'donations',
    'payroll',
    'time-tracking',
    'multi-currency',
    'crm',
    'tasks',
    'collaboration',     // Access — invite employees / accountant / auditor
    'integrations',      // Integrations: every connection as a tile (connect-1)
    'import',            // Import Data (Tally/Zoho/spreadsheet migration)
    'developers',        // Developers: keys, webhooks, the documents door, the API (connect-2)
    // Finance-relevant modules surfaced to Standard mode (pure-ops modules stay Expert-only).
    'approvals',
    'dimensions',        // Cost Centers
    'fixed-expenses',    // fixpage: rent, power, the audit fee, as one sheet (left Settings)
    'projects',
    'documents',
    'consolidation',
    'subscriptions',
    'sales-orders',
    'audit',             // Audit: which audit applies, where it stands, the one next thing
    'partner',           // Partner Program — referral commissions
    'statutory-registers', // Members / Directors & KMP for a company, Owners for everybody else
    'roc',               // ROC Compliance map (companies only — org-type gated)
]);

/** Expert sees everything */
const EXPERT_ITEMS: null = null; // null = show everything

/**
 * Check if a nav item should be visible for the given experience level.
 */
export function isNavItemVisible(itemId: string, level: ExperienceLevel): boolean {
    switch (level) {
        case 'business_owner':
            return BUSINESS_OWNER_ITEMS.has(itemId);
        case 'finance_manager':
            return FINANCE_MANAGER_ITEMS.has(itemId);
        case 'expert':
            return true; // Expert sees everything
        default:
            return true;
    }
}

// ─────────────────────────────────────────────
// Sidebar Label Overrides per Level
// ─────────────────────────────────────────────
// In Simple, we rename sidebar items to plain language.

const BUSINESS_OWNER_LABELS: Record<string, string> = {
    'opening-balances': 'Starting Balances',
    'sales': 'Invoices',
    'purchases': 'Bills',
    'money-in': 'Money Received',
    'money-out': 'Payments Made',
    'expenses': 'Expenses',
    'items': 'Products & Services',
    'reports': 'Reports',
    'receipt-scanner': 'Scan Receipts',
    'ai-tools': 'AI Help',
    'payment-links': 'Collect Online',
    'audit': 'Audit',
    'partner': 'Refer & Earn',
};

const FINANCE_MANAGER_LABELS: Record<string, string> = {
    // Finance managers mostly use standard terms, with minor tweaks
    'payment-links': 'Payment Links',
};

/**
 * Get the display label for a nav item, adapted to the user's level.
 */
export function getNavItemLabel(itemId: string, defaultLabel: string, level: ExperienceLevel): string {
    switch (level) {
        case 'business_owner':
            return BUSINESS_OWNER_LABELS[itemId] || defaultLabel;
        case 'finance_manager':
            return FINANCE_MANAGER_LABELS[itemId] || defaultLabel;
        case 'expert':
            return defaultLabel;
        default:
            return defaultLabel;
    }
}

// ob-2 removed BUSINESS_OWNER_ZONE_LABELS / BUSINESS_OWNER_HIDDEN_ZONES. They
// were unreferenced, and they described a Simple nav ("Money In / Money Out /
// Contacts / Tools" zones) that stopped existing two lanes ago. A stale map of
// the product is worse than no map. Simple's shape now lives in one place:
// `src/shared/config/simpleNav.ts`.

// Unused variable suppression
void EXPERT_ITEMS;
